import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useAllOrganisationsAPI from './useAllOrganisationsAPI';
import useCreateNewRoleAPI from './useCreateNewRoleAPI';
import usePermissionsCategoryAPI from './usePermissionsCategoryAPI';
import useGetCustomRoleByIdAPI from './useGetCustomRoleByIdAPI';
import useEditCustomRoleAPI from './useEditCustomRoleAPI';
import { Organisation } from '../../../../apollo/queries';
import { ROUTE_RNP } from '../../../Routing/routeHelpers';
import { RNPTabs } from '../../../../pages/RolesAndPermissions/types';
import { CustomRoleResponse } from '../../../../apollo/queries/v3/RolesNPermissions/getAllCustomRolesNPermissions.queries';
import {
  CustomRole,
  PermissionToCategory,
} from '../../../../apollo/queries/v3/RolesNPermissions/getCustomRoleById.queries';
import { CreateNewRoleInput } from '../../../../apollo/queries/v3/RolesNPermissions/createNewRole.queries';
import { is1dArrayEqual } from '../../../../utils/1dArrayEquality';
import { RNPToastHelper } from '../../RNPToaster';
import { capitalizeString } from '../../../../utils/StringHelper';
import appLocalStorage from '../../../../utils/AppLocalStorage';

type Props = {
  pageToEdit?: RNPTabs;
  isEdit?: boolean;
};

const useCreateNewRole = ({ pageToEdit, isEdit }: Props) => {
  const history = useHistory<{ role: CustomRoleResponse }>();
  const params = useParams<{ roleId: string }>();

  const redirectToCustomRoleListPage = () =>
    history.push(ROUTE_RNP.customRoles);

  const [selectedOrganisation, setSelectedOrganisation] = useState<
    Pick<Organisation, 'name' | 'code'>
  >({ name: '', code: '' });
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const { organisations } = useAllOrganisationsAPI();
  const [createNewRole] = useCreateNewRoleAPI();
  const roleToEditRef = useRef<CustomRole | null>(null);

  const [editUserPermissions, setEditUserPermissions] = useState<
    PermissionToCategory[]
  >([]);

  const permissionCategoryData = usePermissionsCategoryAPI({
    editUserPermissions,
  });
  const { assignedPermissions } = permissionCategoryData;

  const getIsFormValidForEditRole = () => {
    const roleToEdit = roleToEditRef.current;
    const getPermission = (p: any) => p.permission;

    return !Boolean(
      isEdit &&
        roleToEdit &&
        roleToEdit.name === roleName &&
        roleToEdit.description === roleDescription &&
        is1dArrayEqual(
          roleToEdit.permissionsToCategories.map(getPermission),
          assignedPermissions.map(getPermission),
        ),
    );
  };

  const getIsFormValid = () =>
    selectedOrganisation.code !== '' &&
    roleName !== '' &&
    roleDescription !== '' &&
    assignedPermissions.length > 0 &&
    getIsFormValidForEditRole();

  const updateData = (role: CustomRole) => {
    setRoleName(role.name);
    setRoleDescription(role.description);
    setSelectedOrganisation(role.organisation);
    setEditUserPermissions(
      role.permissionsToCategories.map((ptc) => ({
        ...ptc,
        category: capitalizeString(ptc.category),
      })),
    );
  };

  const [getCustomRole, { loading }] = useGetCustomRoleByIdAPI({
    onComplete: (customRole) => {
      roleToEditRef.current = customRole;
      updateData(customRole);
    },
  });

  const [editCustomRole] = useEditCustomRoleAPI({
    onComplete: () => {
      redirectToCustomRoleListPage();
      RNPToastHelper.updateCustomRoleSuccess({
        roleName: roleName,
      });
    },
  });

  useEffect(() => {
    if (isEdit) {
      getCustomRole(params.roleId);
    }
  }, []);

  const handleUpdateRole = (input: Partial<CreateNewRoleInput>) => {
    delete input.organisation;
    switch (pageToEdit) {
      case RNPTabs.CUSTOM_ROLES: {
        editCustomRole({
          ...input,
          id: params.roleId,
        });
      }
    }
  };

  const onCreateRole = () => {
    if (getIsFormValid()) {
      const { code, name } = selectedOrganisation;
      const input: CreateNewRoleInput = {
        name: roleName,
        description: roleDescription,
        permissions: Array.from(
          new Set(assignedPermissions.map((p) => p.permission)),
        ),
        organisation: { code, name },
      };

      if (isEdit) {
        handleUpdateRole(input);
      } else {
        createNewRole(input, redirectToCustomRoleListPage);
      }
    }
  };

  return {
    loading,
    organisations,
    permissionCategoryData,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    formData: {
      selectedOrganisation,
      roleName,
      roleDescription,
      getIsFormValid,
      onRoleDescriptionChange: setRoleDescription,
      onRoleNameChange: setRoleName,
      onSelectedOrganisationChange: setSelectedOrganisation,
      onCancel: redirectToCustomRoleListPage,
      onCreateRole,
    },
  };
};

export default useCreateNewRole;
